import { DefaultChatTransport, type ChatTransport, type UIMessage } from "ai"

import { socketReady } from "./connection"
import { SocketChatTransport } from "./socket-chat-transport"

/**
 * Socket.IO when it is available, the HTTP route otherwise.
 *
 * Decided per turn, not once at mount: the socket may come up late or drop, and
 * the advisor should not have to reload for the chat to work. The HTTP route stays
 * exactly as it was, which is what makes this switch cheap to revert — and its
 * SSE framing already survives the gateway, it just arrives in one block there.
 */
export class FallbackChatTransport<UI_MESSAGE extends UIMessage>
  implements ChatTransport<UI_MESSAGE>
{
  private readonly overSocket: SocketChatTransport<UI_MESSAGE> | null
  private readonly overHttp: DefaultChatTransport<UI_MESSAGE>

  constructor(options: {
    /** Server lane, or null while a route has not moved over yet. */
    lane: string | null
    /** The HTTP endpoint this chat used before. */
    api: string
    /** Extra body fields, read fresh on every send. */
    body?: () => Record<string, unknown> | undefined
  }) {
    this.overSocket = options.lane
      ? new SocketChatTransport<UI_MESSAGE>({
          lane: options.lane,
          body: options.body,
        })
      : null

    this.overHttp = new DefaultChatTransport<UI_MESSAGE>({
      api: options.api,
      credentials: "same-origin",
      prepareSendMessagesRequest: ({ messages }) => ({
        body: {
          ...options.body?.(),
          messages: messages.map((message) => ({
            role: message.role,
            content: message.parts
              .filter((part) => part.type === "text")
              .map((part) => (part as { text: string }).text)
              .join(""),
          })),
        },
      }),
    })
  }

  async sendMessages(
    options: Parameters<ChatTransport<UI_MESSAGE>["sendMessages"]>[0],
  ) {
    if (this.overSocket && (await socketReady())) {
      return this.overSocket.sendMessages(options)
    }
    return this.overHttp.sendMessages(options)
  }

  reconnectToStream(
    options: Parameters<ChatTransport<UI_MESSAGE>["reconnectToStream"]>[0],
  ) {
    return this.overHttp.reconnectToStream(options)
  }
}
